import { Route, Routes, useLocation, useNavigate } from "react-router";
import { Header } from "@/widgets/page-header";
import { LazyMotion } from "framer-motion";
import { Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { domAnimation } from "@/features";
import { mainLayoutRouteElements } from "@/app/mainLayoutRoutes";
import { useLogoutInFlight } from "@/shared/lib/logoutInFlight";
import {
  consumeAuthReturnPath,
  consumeMarketingHandoffFromLocation,
  getHandoffDestination,
  getStoredMarketingHandoff,
  parseMarketingHandoff,
  peekPlanCheckout,
  storeMarketingHandoff,
} from "@/shared/lib/marketingHandoff";
import {
  captureReadingDraftFromLocation,
  peekReadingDraft,
  READING_DRAFT_PATH,
} from "@/shared/lib/readingDraft";

import "./App.css";

// Этот компонент проверяет авторизацию и перенаправляет
const AuthGate = () => {
  const { isAuthenticated, isLoading, login } = useKindeAuth();
  const navigate = useNavigate();
  const loggingOut = useLogoutInFlight();
  const loginStarted = useRef(false);

  useEffect(() => {
    if (isLoading || loggingOut) return;

    if (isAuthenticated) {
      const destination = peekReadingDraft()
        ? READING_DRAFT_PATH
        : getHandoffDestination(getStoredMarketingHandoff());
      navigate(destination, { replace: true });
    } else {
      const fromUrl = parseMarketingHandoff(window.location.search);
      if (fromUrl) {
        storeMarketingHandoff(fromUrl);
      }

      if (loginStarted.current) {
        return;
      }

      loginStarted.current = true;
      // redirectURL is the OAuth callback. It must stay on /ask, where
      // KindeProvider exchanges the code. A saved tariff is applied after login.
      void login();
    }
  }, [isAuthenticated, isLoading, loggingOut, login, navigate]);

  return (
    <div className="h-screen grid place-content-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
    </div>
  );
};

function App() {
  const { isAuthenticated, isLoading } = useKindeAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const loggingOut = useLogoutInFlight();
  // Resume the draft once per page load (OAuth callback or payment return),
  // so later in-app visits to /ask don't bounce back to /draft.
  const draftResumed = useRef(false);

  useLayoutEffect(() => {
    consumeMarketingHandoffFromLocation();
    // Before AuthGate calls login(): the fragment is lost on the Kinde redirect.
    captureReadingDraftFromLocation();
  }, []);

  useLayoutEffect(() => {
    if (isLoading || loggingOut || !isAuthenticated) {
      return;
    }

    // Only resume from the OAuth callback. Consuming the path on /pricing or
    // /payment/complete would drop it before Kinde navigates away.
    if (location.pathname === "/" || location.pathname === "/ask") {
      const returnPath = consumeAuthReturnPath();
      if (returnPath && returnPath !== `${location.pathname}${location.search}`) {
        navigate(returnPath, { replace: true });
        return;
      }

      // A reading prepared on the landing waits for confirmation after login
      // and after a top-up (payment complete returns to /ask).
      if (!draftResumed.current && peekReadingDraft()) {
        draftResumed.current = true;
        navigate(READING_DRAFT_PATH, { replace: true });
        return;
      }
    }

    if (location.pathname === "/pricing" || !peekPlanCheckout()) {
      return;
    }
    navigate("/pricing", { replace: true });
  }, [isAuthenticated, isLoading, location.pathname, location.search, loggingOut, navigate]);

  return (
    <Suspense fallback={null}>
      <LazyMotion features={domAnimation}>
        <Routes>
          <Route element={<Header />}>{mainLayoutRouteElements}</Route>
          {/* The Kinde callback route is added here. It's outside of the main
            layout with the Header, as it's a transient page. */}
          <Route path="/" element={<AuthGate />} />
        </Routes>
      </LazyMotion>
    </Suspense>
  );
}

export { App };
